import React, { useMemo, useEffect, forwardRef, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Quill from 'quill';
import ImageResize from 'quill-image-resize-module-react';

Quill.register('modules/imageResize', ImageResize);

const QuillEditor = forwardRef(({ content, setContent, handlePaste, imageHandler, styles, t }, ref) => {
  // Use a local ref for the Quill instance to manage it internally
  const quillInstanceRef = useRef(null);

  // Set the forwarded ref to point to the local ref
  // This allows the parent to access the same instance
  useEffect(() => {
    if (typeof ref === 'function') {
      ref(quillInstanceRef.current);
    } else if (ref) {
      ref.current = quillInstanceRef.current;
    }
  }, [ref]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ align: [] }],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      // Use the memoized imageHandler from the parent
      handlers: { image: imageHandler },
    },
    imageResize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize'],
    },
    clipboard: {
      // matchVisual: true를 설정하면 HTML을 붙여넣을 때 보이는 스타일을 최대한 유지합니다.
      matchVisual: true,
    },


  }), [imageHandler]);

  const formats = [
    'size', 'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'script', 'blockquote', 'code-block',
    'list', 'bullet', 'ordered', 'indent', 'direction', 'align',
    'link', 'image', 'video', 'clean',
  ];

  useEffect(() => {
    // This effect runs whenever `handlePaste` or the `ref` changes.
    const editor = quillInstanceRef.current?.getEditor();
    if (!editor) return;

    editor.root.addEventListener('paste', handlePaste);
    return () => editor.root.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  // Use a new useEffect to specifically handle language changes.
  useEffect(() => {
    const editor = quillInstanceRef.current?.getEditor();
    if (editor && editor.scroll) {
      // Get the current Quill content.
      const currentContent = editor.getContents();
      // Update the placeholder.
      editor.root.dataset.placeholder = t('writePost.contentPlaceholder');
      // Set the content back, ensuring the editor's state is preserved.
      editor.setContents(currentContent);
    }
  }, [t]);

  return (
    <div className={styles.editorWrapper}>
      <ReactQuill
        ref={quillInstanceRef}
        value={content}
        onChange={setContent}
        modules={modules}
        formats={formats}
        placeholder={t('writePost.contentPlaceholder')}
        className={styles.editor}
      />
    </div>
  );
});

export default React.memo(QuillEditor);